import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RiArrowRightLine, RiCheckLine, RiLockLine } from '@remixicon/react'
import {
  ONBOARDING_LIMITS,
  countAnswered,
  validateSubmission,
  type AnswerValue,
  type OnboardingPublicView,
} from 'shared'
import { Alert, Button, Card, Field, Input } from '../components/ui'
import QuestionField from '../components/survey/QuestionField'
import { ApplyShell, Stepper, WizardActions, type WizardStep } from '../components/onboarding/ApplyParts'
import { api, ApiError } from '../utils/api'
import { toErrorMessage } from '../lib/errors'
import { formatDate } from '../lib/dates'
import { loadTurnstile } from '../lib/turnstile'
import { useNoReferrer } from '../lib/useNoReferrer'

/**
 * The page a new company lands on to apply: a landing with the code at its
 * centre, then a three-step wizard (who you are, the questionnaire, confirm and
 * send). The answers stay in the browser until the last step, so nothing is
 * stored about someone who walks away halfway.
 *
 * It is the second place in ScaleBrain an unauthenticated person lands on
 * purpose, and it says what happens next: nothing is created until an operator
 * accepts.
 */

type Step = 'code' | WizardStep | 'done'

function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
}

/** A line of the summary: what it is, and what was written. */
function RecapRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2.5">
      <dt className="text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="truncate font-medium text-gray-900 dark:text-gray-50">{value}</dd>
    </div>
  )
}

export default function Onboarding() {
  const { t, i18n } = useTranslation()
  useNoReferrer()

  const [step, setStep] = useState<Step>('code')
  const [code, setCode] = useState('')
  const [view, setView] = useState<OnboardingPublicView | null>(null)
  const [checking, setChecking] = useState(false)
  // `undefined` while it loads: the headline stays invisible rather than flash the default.
  const [landingTitle, setLandingTitle] = useState<string | null | undefined>(undefined)

  const [company, setCompany] = useState('')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [detailsTried, setDetailsTried] = useState(false)

  const [answers, setAnswers] = useState<Record<string, AnswerValue>>({})
  const [accepted, setAccepted] = useState(false)
  const [proof, setProof] = useState<string | null>(null)
  const [showErrors, setShowErrors] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const widgetRef = useRef<HTMLDivElement | null>(null)
  const widgetId = useRef<string | undefined>(undefined)
  const headingRef = useRef<HTMLHeadingElement | null>(null)

  const siteKey = view?.turnstile_site_key ?? null

  useEffect(() => {
    let cancelled = false
    // A slow answer must not hide the page's heading: after a moment the built-in text shows.
    const giveUp = window.setTimeout(() => setLandingTitle((current) => current ?? null), 1500)
    api
      .get<{ data: { title: string | null } }>('/api/public/onboarding/landing')
      .then((response) => !cancelled && setLandingTitle(response.data.title))
      .catch(() => !cancelled && setLandingTitle(null))
    return () => {
      cancelled = true
      window.clearTimeout(giveUp)
    }
  }, [])

  // Moving to another step moves the focus to its heading: a keyboard or screen
  // reader user would otherwise be left on a button that is gone. The landing keeps
  // the focus the code field takes for itself.
  useEffect(() => {
    if (step !== 'code') headingRef.current?.focus()
  }, [step])

  // The widget lives on the last step only, and is drawn again each time that
  // step comes back (the container is a new element).
  useEffect(() => {
    if (step !== 'confirm' || !siteKey) return

    let cancelled = false

    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !widgetRef.current || widgetId.current !== undefined) return
        widgetId.current = turnstile.render(widgetRef.current, {
          sitekey: siteKey,
          callback: (value) => setProof(value),
          'expired-callback': () => setProof(null),
          'error-callback': () => setProof(null),
        })
      })
      .catch(() => setError(t('publicSurvey.antibotUnavailable')))

    return () => {
      cancelled = true
    }
  }, [step, siteKey, t])

  const errors = useMemo(() => {
    if (!view) return {}
    return Object.fromEntries(
      validateSubmission(view.questions, answers).map((entry) => [
        entry.question_id,
        t(`survey.errors.${entry.code}`, { ...entry.params }),
      ]),
    )
  }, [view, answers, t])

  const answered = view ? countAnswered(view.questions, answers) : 0

  /** Leaving the last step takes its anti-bot widget with it. */
  const leaveConfirm = () => {
    widgetId.current = undefined
    setProof(null)
  }

  const goTo = (next: Step) => {
    if (step === 'confirm' && next !== 'confirm') leaveConfirm()
    setError(null)
    setStep(next)
    window.scrollTo({ top: 0 })
  }

  /**
   * Another session is another questionnaire and another notice: what was
   * answered, flagged or accepted for the one before does not carry over.
   */
  const forgetSession = () => {
    setView(null)
    setAnswers({})
    setAccepted(false)
    setShowErrors(false)
    leaveConfirm()
  }

  const checkCode = async (event: React.FormEvent) => {
    event.preventDefault()
    setChecking(true)
    setError(null)

    try {
      const response = await api.post<{ data: OnboardingPublicView }>('/api/public/onboarding/check', {
        code,
      })
      forgetSession()
      setView(response.data)
      goTo('details')
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) setError(t('onboardingApply.invalidCode'))
      else if (err instanceof ApiError && err.status === 429) setError(t('onboardingApply.tooMany'))
      else setError(toErrorMessage(err) ?? t('errors.generic'))
    } finally {
      setChecking(false)
    }
  }

  const fromDetails = (event: React.FormEvent) => {
    event.preventDefault()
    setDetailsTried(true)
    if (!company.trim() || !name.trim() || !email.trim() || !looksLikeEmail(email) || !accepted) return
    goTo('survey')
  }

  const fromSurvey = (event: React.FormEvent) => {
    event.preventDefault()
    setShowErrors(true)

    const firstInvalid = view?.questions.find((question) => errors[question.id])
    if (firstInvalid) {
      setError(t('survey.fixErrors'))
      // The flagged question may be far above the button that was pressed.
      const field = document.getElementById(`q-${firstInvalid.id}`)
      field?.scrollIntoView({ block: 'center' })
      field?.focus({ preventScroll: true })
      return
    }
    goTo('confirm')
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    // Enter pressed twice must not send twice: a proof is good once.
    if (!view || submitting) return

    setError(null)

    if (siteKey && !proof) {
      setError(t('publicSurvey.antibotRequired'))
      return
    }

    setSubmitting(true)
    try {
      await api.post('/api/public/onboarding/submit', {
        code,
        company_name: company,
        name,
        email,
        answers,
        privacy_accepted: true,
        ...(siteKey ? { turnstile_token: proof } : {}),
      })
      goTo('done')
    } catch (err) {
      if (err instanceof ApiError && err.code === 'antibot_failed') {
        // A proof is good once: a failed one needs a new challenge.
        setProof(null)
        window.turnstile?.reset(widgetId.current)
        setError(t('publicSurvey.antibotFailed'))
      } else if (err instanceof ApiError && err.code === 'already_applied') {
        setError(t('onboardingApply.alreadyApplied'))
      } else if (err instanceof ApiError && err.status === 404) {
        // The session closed or filled while the applicant was writing.
        forgetSession()
        goTo('code')
        setError(t('onboardingApply.invalidCode'))
      } else {
        setError(toErrorMessage(err) ?? t('errors.generic'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  // --- The landing: the code, at the centre.
  if (step === 'code') {
    return (
      <ApplyShell width="max-w-xl" centered>
        <p className="mx-auto mb-4 w-fit rounded-sm border border-gray-300 bg-gray-100 px-2 py-[3px] text-micro font-medium uppercase text-primary-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-50">
          {t('onboardingApply.eyebrow')}
        </p>
        <h1
          className={`text-center font-display text-4xl font-normal tracking-tight text-gray-950 dark:text-gray-50 sm:text-5xl ${
            landingTitle === undefined ? 'invisible' : ''
          }`}
        >
          {landingTitle ?? t('onboardingApply.title')}
        </h1>
        <p className="mx-auto mt-4 max-w-md text-center text-base text-gray-600 dark:text-gray-300">
          {t('onboardingApply.subtitle')}
        </p>

        <form onSubmit={checkCode} noValidate className="mt-10">
          <Card className="space-y-4 p-5 shadow-lg sm:p-8">
            <label
              htmlFor="onboarding-code"
              className="block text-center text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t('onboardingApply.code')}
            </label>
            <Input
              id="onboarding-code"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              maxLength={16}
              autoFocus
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="characters"
              spellCheck={false}
              aria-describedby="onboarding-code-hint"
              className="h-14 px-2 text-center font-mono text-xl uppercase tracking-[0.15em] sm:text-2xl sm:tracking-[0.3em]"
              placeholder="XXXXX-XXXXX"
            />
            <p id="onboarding-code-hint" className="text-center text-xs text-gray-500 dark:text-gray-400">
              {t('onboardingApply.codeHint')}
            </p>
            {error && <Alert>{error}</Alert>}
            <Button type="submit" size="lg" className="w-full" loading={checking} disabled={!code.trim()}>
              {t('onboardingApply.continue')}
              <RiArrowRightLine className="-mr-1 ml-2 size-5" aria-hidden="true" />
            </Button>
          </Card>
        </form>

        <ol className="mt-10 grid gap-4 text-sm sm:grid-cols-3">
          {(['how1', 'how2', 'how3'] as const).map((key, position) => (
            <li key={key} className="flex gap-3 sm:flex-col sm:gap-2">
              <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-white text-xs font-semibold text-primary-700 ring-1 ring-primary-200 dark:bg-gray-900 dark:text-primary-300 dark:ring-primary-900">
                {position + 1}
              </span>
              <span className="text-gray-600 dark:text-gray-300">{t(`onboardingApply.${key}`)}</span>
            </li>
          ))}
        </ol>
      </ApplyShell>
    )
  }

  // --- Sent.
  if (step === 'done') {
    return (
      <ApplyShell width="max-w-xl" centered>
        <Card className="p-8 text-center shadow-lg">
          <span className="mx-auto flex size-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:ring-emerald-500/30">
            <RiCheckLine className="size-8" aria-hidden="true" />
          </span>
          <h1
            ref={headingRef}
            tabIndex={-1}
            className="mt-5 text-2xl font-semibold text-gray-950 outline-none dark:text-white"
          >
            {t('onboardingApply.doneTitle')}
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            {t('onboardingApply.doneDescription', { email: email.trim().toLowerCase() })}
          </p>
          <p className="mt-6 text-xs text-gray-500 dark:text-gray-400">{t('onboardingApply.doneClose')}</p>
        </Card>
      </ApplyShell>
    )
  }

  // --- The wizard.
  const until = view
    ? t('onboardingApply.openUntil', { date: formatDate(view.expires_at.slice(0, 10), i18n.language) })
    : null
  const heading = 'scroll-mt-6 text-lg font-semibold text-gray-900 outline-none dark:text-gray-50'

  return (
    <ApplyShell aside={until}>
      <h1 className="font-display text-2xl font-normal tracking-tight text-gray-950 dark:text-gray-50 sm:text-3xl">
        {view?.name}
      </h1>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 sm:hidden">{until}</p>
      <div className="mt-6">
        <Stepper current={step} />
      </div>

      {step === 'details' && (
        <form onSubmit={fromDetails} noValidate className="mt-8">
          <Card className="space-y-5 p-5 shadow-md sm:p-8">
            <div>
              <h2 ref={headingRef} tabIndex={-1} className={heading}>
                {t('onboardingApply.detailsTitle')}
              </h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                {t('onboardingApply.detailsIntro')}
              </p>
            </div>
            <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-900/50">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-50">
                <RiLockLine className="size-4 text-gray-400" aria-hidden="true" />
                {t('publicSurvey.privacyTitle')}
              </h3>
              {/* Plain text written by whoever opened the session: shown as text, never as markup. */}
              <p className="max-h-48 overflow-y-auto whitespace-pre-wrap text-sm text-gray-600 dark:text-gray-300">
                {view?.privacy_notice}
              </p>
              <label className="flex min-h-10 items-start gap-3 py-1 text-sm">
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(event) => setAccepted(event.target.checked)}
                  aria-invalid={detailsTried && !accepted}
                  className="mt-0.5 size-4 rounded border-gray-300 accent-primary-600 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-900"
                />
                <span className="text-gray-900 dark:text-gray-50">{t('publicSurvey.accept')}</span>
              </label>
              {detailsTried && !accepted && (
                <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                  {t('publicSurvey.acceptRequired')}
                </p>
              )}
            </div>
            <Field label={t('onboardingApply.company')} required>
              <Input
                value={company}
                maxLength={ONBOARDING_LIMITS.maxNameLength}
                autoComplete="organization"
                aria-invalid={detailsTried && !company.trim()}
                error={detailsTried && !company.trim() ? t('onboardingApply.fieldRequired') : undefined}
                onChange={(event) => setCompany(event.target.value)}
              />
            </Field>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label={t('onboardingApply.name')} required>
                <Input
                  value={name}
                  maxLength={ONBOARDING_LIMITS.maxNameLength}
                  autoComplete="name"
                  aria-invalid={detailsTried && !name.trim()}
                  error={detailsTried && !name.trim() ? t('onboardingApply.fieldRequired') : undefined}
                  onChange={(event) => setName(event.target.value)}
                />
              </Field>
              <Field label={t('onboardingApply.email')} hint={t('onboardingApply.emailHint')} required>
                <Input
                  type="email"
                  value={email}
                  maxLength={254}
                  autoComplete="email"
                  aria-invalid={detailsTried && !looksLikeEmail(email)}
                  error={
                    detailsTried && !looksLikeEmail(email)
                      ? t(email.trim() ? 'onboardingApply.emailInvalid' : 'onboardingApply.fieldRequired')
                      : undefined
                  }
                  onChange={(event) => setEmail(event.target.value)}
                />
              </Field>
            </div>
            <WizardActions onBack={() => goTo('code')} next={t('onboardingApply.continue')} />
          </Card>
        </form>
      )}

      {step === 'survey' && view && (
        <form onSubmit={fromSurvey} noValidate className="mt-8">
          <Card className="shadow-md">
            <div className="border-b border-gray-100 p-5 dark:border-gray-800 sm:px-8 sm:py-6">
              <h2 ref={headingRef} tabIndex={-1} className={heading}>
                {view.title}
              </h2>
              {view.description && (
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{view.description}</p>
              )}
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {t('onboardingApply.progress', { done: answered, total: view.questions.length })}
              </p>
            </div>
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {view.questions.map((question) => (
                <div key={question.id} className="p-5 sm:px-8 sm:py-6">
                  <QuestionField
                    question={question}
                    value={answers[question.id] ?? null}
                    onChange={(value) => setAnswers((current) => ({ ...current, [question.id]: value }))}
                    error={showErrors ? errors[question.id] : undefined}
                  />
                </div>
              ))}
            </div>
            <div className="space-y-4 border-t border-gray-100 p-5 dark:border-gray-800 sm:px-8">
              {error && <Alert>{error}</Alert>}
              <WizardActions onBack={() => goTo('details')} next={t('onboardingApply.continue')} />
            </div>
          </Card>
        </form>
      )}

      {step === 'confirm' && view && (
        <form onSubmit={submit} noValidate className="mt-8 space-y-6">
          <Card className="p-5 shadow-md sm:p-8">
            <h2 ref={headingRef} tabIndex={-1} className={heading}>
              {t('onboardingApply.recapTitle')}
            </h2>
            <dl className="mt-4 divide-y divide-gray-100 text-sm dark:divide-gray-800">
              <RecapRow label={t('onboardingApply.company')} value={company} />
              <RecapRow label={t('onboardingApply.name')} value={name} />
              <RecapRow label={t('onboardingApply.email')} value={email} />
              <RecapRow
                label={t('onboardingApply.stepSurvey')}
                value={t('onboardingApply.progress', { done: answered, total: view.questions.length })}
              />
            </dl>
            <div className="mt-3 flex flex-wrap gap-x-4 text-sm">
              {(
                [
                  ['details', 'onboardingApply.editDetails'],
                  ['survey', 'onboardingApply.editAnswers'],
                ] as const
              ).map(([target, label]) => (
                <button
                  key={target}
                  type="button"
                  onClick={() => goTo(target)}
                  className="inline-flex min-h-10 items-center rounded font-medium text-primary-600 hover:underline focus-visible:outline-2 focus-visible:outline-primary-500 dark:text-primary-400"
                >
                  {t(label)}
                </button>
              ))}
            </div>
          </Card>

          <Card className="space-y-4 p-5 shadow-md sm:p-8">
            <p className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
              <RiCheckLine className="size-4 text-green-600" aria-hidden="true" />
              {t('onboardingApply.privacyAccepted')}
            </p>
            {siteKey && <div ref={widgetRef} />}
            {error && <Alert>{error}</Alert>}
            <WizardActions
              onBack={() => goTo('survey')}
              next={t('onboardingApply.send')}
              loading={submitting}
              arrow={false}
            />
          </Card>
        </form>
      )}
    </ApplyShell>
  )
}
